import {
  AUTO_CMS_EDITOR_PANEL_BASENAME,
  findFirstItemMatchingDynamicPage,
  getInnerRouteForDynamicPageItem,
  getPageLinkField,
} from '@wix/wix-data-client-common'
import { checkIfRoutePatternIncludesFields } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/checkIfRoutePatternIncludesFields'
import { getCollectionIdAndPatternOfCurrentDynamicPage } from '@wix/wix-data-client-common/dist/esm/business-logic/dynamic-pages/getCollectionIdAndPatternOfCurrentDynamicPage'
import getPanelUrl from '@wix/wix-data-client-common/dist/esm/panels/getPanelUrl'

const autoCMSPathDescriptor = ({
  parts: {
    collectionId,
    isNew,
    itemId,
    settings,
    collectionsGroup,
    importRequested,
  } = {},
  origin,
  hostname,
  locale,
  editorSdkProxy,
}) => ({
  parts: {
    collectionId,
    isNew,
    itemId,
    settings,
    collectionsGroup,
    importRequested,
  },
  origin,
  hostname,
  locale,
  editorSdkProxy,
})

const autoCMSEditorPanelURL = async (
  {
    parts: {
      collectionId,
      isNew,
      itemId,
      settings,
      collectionsGroup,
      importRequested,
    },
    origin,
    hostname,
    editorSdkProxy,
  },
  appApi,
) => {
  const params = { origin }

  if (typeof collectionId === 'string') {
    params.collectionId = encodeURIComponent(collectionId)

    if (isNew) {
      params.isNew = true
    } else if (typeof itemId === 'string') {
      params.itemId = encodeURIComponent(itemId)
    } else if (importRequested) {
      params.importRequested = true
    }
  } else if (settings) {
    params.settings = true
  } else if (typeof collectionsGroup === 'string') {
    params.collectionsGroup = encodeURIComponent(collectionsGroup)
  }

  const instance = await appApi.getInstance()
  params.instance = instance

  const panelURL = await getPanelUrl({
    hostname,
    panelBasename: AUTO_CMS_EDITOR_PANEL_BASENAME,
    relativeUrl: './editor-panel-app.html',
    params,
    editorSdkProxy,
    panelWithSeparateGaLifecycle: true,
  })

  return panelURL
}

const openAutoCMSPanel = async ({
  editorSdkProxy,
  pathDescriptor,
  i18n,
  appApi,
}) => {
  const result = await editorSdkProxy.editor.openModalPanel({
    title: i18n.t('Content_Manager_Panel_Title'),
    url: await autoCMSEditorPanelURL(pathDescriptor, appApi),
    width: '94%',
    height: '94%',
    centered: true,
    noPanelPadding: true,
  })

  // In Fake Editor getAppAPI is async, in real editor it's sync
  Promise.resolve(editorSdkProxy.editor.getAppAPI()).then(appApi => {
    appApi.emit('contentManagerClosed')
  })

  return result
}

const _openAutoCMS = async ({
  editorSdkProxy,
  pathDescriptor,
  i18n,
  logger,
  appApi,
}) => {
  const result = await openAutoCMSPanel({
    editorSdkProxy,
    pathDescriptor,
    i18n,
    appApi,
  })

  appApi
    .refreshCollectionItemCounts()
    .catch(err =>
      logger.error(
        new Error('Failed to refresh collection item counts', { cause: err }),
      ),
    )

  appApi.refreshSchemasCache()

  updateEditorView(editorSdkProxy)

  return result
}

const openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded = async ({
  wixData,
  editorSdkProxy,
  collectionsApi,
  buildPathDescriptor,
  i18n,
  logger,
  appApi,
}) => {
  const fallbackPathParts = {}

  const collectionIdAndPattern =
    await getCollectionIdAndPatternOfCurrentDynamicPage({ editorSdkProxy })
  const currentPageIsValidDynamicPage = collectionIdAndPattern !== null
  if (currentPageIsValidDynamicPage) {
    const { collectionId, pattern } = collectionIdAndPattern
    Object.assign(fallbackPathParts, { collectionId })

    const routePatternIncludesFields =
      checkIfRoutePatternIncludesFields(pattern)
    // If a pattern does not include a field, the page is considered an index page.
    // All items have the same links to an index page so the inner route does not change as a result of changing an item matching the dynamic page.
    if (routePatternIncludesFields) {
      const dynamicRouting =
        await editorSdkProxy.document.routers.getCurrentDynamicRouting()

      const { innerRoute, prefix } = dynamicRouting || {}
      const innerRouteIsDefined = typeof innerRoute !== 'undefined'
      // The inner route can be undefined for different reasons:
      // - there are no items in the collection,
      // - there are items in the collection but the routing data has not yet been loaded,
      // - the dynamic page is broken (there is no route pattern for the page).
      if (innerRouteIsDefined) {
        let pageLinkField
        try {
          const fields = (await collectionsApi.get(collectionId)).fields
          pageLinkField = getPageLinkField(fields, prefix, pattern)
        } catch {
          pageLinkField = null
        }
        const item = await findFirstItemMatchingDynamicPage({
          collectionId,
          prefix,
          innerRoute,
          wixData,
          pageLinkField,
        })
        if (item) {
          const itemId = item._id

          const valueReturnedByAutoCMS = await _openAutoCMS({
            editorSdkProxy,
            pathDescriptor: buildPathDescriptor({ collectionId, itemId }),
            i18n,
            logger,
            appApi,
          })

          let changedInnerRouteIfAny

          const innerRouteOfItem = await getInnerRouteForDynamicPageItem({
            collectionId,
            itemId,
            pageLinkField,
            prefix,
            wixData,
          })
          if (
            typeof innerRouteOfItem !== 'undefined' &&
            innerRouteOfItem !== innerRoute
          ) {
            changedInnerRouteIfAny = innerRouteOfItem
          }

          await updateEditorView(editorSdkProxy, changedInnerRouteIfAny)

          return valueReturnedByAutoCMS
        }
      }
    }
  }

  return _openAutoCMS({
    editorSdkProxy,
    pathDescriptor: buildPathDescriptor(fallbackPathParts),
    i18n,
    logger,
    appApi,
  })
}

const updateEditorView = (editorSdkProxy, selectedRoute) =>
  editorSdkProxy.editor.routers
    .refresh({
      selectedRoute,
    })
    .then(() => {
      //TODO: check do we need it in TB
      editorSdkProxy.document.application.livePreview.refresh({
        source: 'AFTER_DB_CHANGE',
        shouldFetchData: true,
      })
      editorSdkProxy.editor.updateAppState({
        lastDataUpdateAt: Date.now(),
      })
    })
    .catch(() => {}) // Editor X does not have implementation for it yet

export const openAutoCMSNewItemView = ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
}) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionId, isNew: true },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
  })

export const openWixServices = async ({ editorSdkProxy }) => {
  await editorSdkProxy.editor.openDashboardPanel({
    url: '/wix-services',
    closeOtherPanels: false,
  })

  updateEditorView(editorSdkProxy)
}

export const openAutoCMSCollectionView = ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
  importRequested,
}) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionId, importRequested },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
  })

export const openAutoCMSCollectionsList = ({
  editorSdkProxy,
  collectionsApi,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  collectionsGroup,
  appApi,
}) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionsGroup },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
  })

export const openAutoCMSAdvancedSettings = ({
  editorSdkProxy,
  collectionsApi,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
}) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { settings: true },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
  })

export const openAutoCMSItemView = ({
  editorSdkProxy,
  collectionsApi,
  collectionId,
  itemId,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
}) =>
  openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: () =>
      autoCMSPathDescriptor({
        parts: { collectionId, itemId },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    wixData,
    i18n,
    logger,
    appApi,
  })

export const openAutoCMSItemViewForCurrentDynamicPage = async ({
  editorSdkProxy,
  collectionsApi,
  origin,
  wixData,
  hostname,
  i18n,
  logger,
  appApi,
}) => {
  return openAutoCMSAndUpdateInnerRouteAfterClosingIfNeeded({
    wixData,
    editorSdkProxy,
    collectionsApi,
    buildPathDescriptor: ({ collectionId, itemId }) =>
      autoCMSPathDescriptor({
        parts: { collectionId, itemId },
        origin,
        hostname,
        locale: i18n.language,
        editorSdkProxy,
      }),
    i18n,
    logger,
    appApi,
  })
}
